import { createAsyncThunk, createSlice, PayloadAction, isAnyOf } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import keyBy from 'lodash/keyBy'
import { getBalanceNumber } from '@pancakeswap/utils/formatBalance'
import { fetchTokenUSDValue } from '@pancakeswap/utils/llamaPrice'
import {
  fetchNftPoolsTimeLimits,
  fetchNftPoolsTotalStaking,
  fetchNftPoolsIsApprovedForAll,
  fetchUserNftBalances,
  fetchUserPendingRewards,
  fetchUserStakedNftBalances,
  getNftPoolsConfig,
  getPoolAprByTokenPerSecond,
  SerializedNftPool,
} from '@pancakeswap/nft-pools'
import { ChainId } from '@pancakeswap/sdk'

import { NftPoolsState } from 'state/types'
import { isAddress } from 'utils'
import { getViemClients } from 'utils/viem'
import { getPoolsPriceHelperLpFiles } from 'config/constants/priceHelperLps/index'

import fetchFarms from '../farms/fetchFarms'
import getFarmsPrices from '../farms/getFarmsPrices'
import { getTokenPricesFromFarm } from './helpers'
import { resetUserState } from '../global/actions'

export const fetchNftPoolsPublicDataAsync = (chainId: number) => async (dispatch, getState) => {
  try {
    const [block, timeLimits] = await Promise.all([
      getViemClients({ chainId })?.getBlock({ blockTag: 'latest' }),
      fetchNftPoolsTimeLimits(chainId, getViemClients),
    ])

    const timeLimitsSousIdMap = keyBy(timeLimits, 'sousId')
    const priceHelperLpsConfig = getPoolsPriceHelperLpFiles(chainId)
    const poolsConfig = getNftPoolsConfig(chainId) || []
    const activePriceHelperLpsConfig = priceHelperLpsConfig.filter((priceHelperLpConfig) => {
      return (
        poolsConfig
          .filter((pool) => pool.earningToken.address.toLowerCase() === priceHelperLpConfig.token.address.toLowerCase())
          .filter((pool) => {
            const poolTimeLimit = timeLimitsSousIdMap[pool.sousId]
            if (poolTimeLimit) {
              return poolTimeLimit.endTimestamp > block.timestamp
            }
            return false
          }).length > 0
      )
    })

    const [totalStakings, poolsWithDifferentFarmToken] = await Promise.all([
      fetchNftPoolsTotalStaking(chainId, getViemClients),
      activePriceHelperLpsConfig.length > 0 ? fetchFarms(priceHelperLpsConfig, chainId) : Promise.resolve([]),
    ])

    const totalStakingsSousIdMap = keyBy(totalStakings, 'sousId')

    const farmsData = getState().farms.data
    const wethUSDCFarm =
      activePriceHelperLpsConfig.length > 0
        ? farmsData.find((farm) => farm.token.symbol === 'USDC' && farm.quoteToken.symbol === 'WETH')
        : null
    const farmsWithPricesOfDifferentTokenPools = wethUSDCFarm
      ? getFarmsPrices([wethUSDCFarm, ...poolsWithDifferentFarmToken], chainId)
      : []

    const prices = getTokenPricesFromFarm([...farmsData, ...farmsWithPricesOfDifferentTokenPools, ...[]])

    const liveData: any[] = []

    for (const pool of poolsConfig) {
      const timeLimit = timeLimitsSousIdMap[pool.sousId]
      const totalStaking = totalStakingsSousIdMap[pool.sousId]
      const isPoolEndBlockExceeded =
        block.timestamp > 0 && timeLimit ? block.timestamp > Number(timeLimit.endTimestamp) : false
      const isPoolFinished = pool.isFinished || isPoolEndBlockExceeded

      const stakingTokenAddress = isAddress(pool.stakingToken.address)
      let stakingTokenPrice = stakingTokenAddress ? prices[stakingTokenAddress] : 0
      if (stakingTokenAddress && !prices[stakingTokenAddress] && !isPoolFinished) {
        // eslint-disable-next-line no-await-in-loop
        const result = await fetchTokenUSDValue(chainId, [stakingTokenAddress])
        stakingTokenPrice = result.get(stakingTokenAddress) || 0
      }

      const earningTokenAddress = isAddress(pool.earningToken.address)
      let earningTokenPrice = earningTokenAddress ? prices[earningTokenAddress] : 0
      if (earningTokenAddress && !prices[earningTokenAddress] && !isPoolFinished) {
        // eslint-disable-next-line no-await-in-loop
        const result = await fetchTokenUSDValue(chainId, [earningTokenAddress])
        earningTokenPrice = result.get(earningTokenAddress) || 0
      }
      // const totalStaked = Number(totalStaking.totalStaked)
      // const apr = !isPoolFinished
      //   ? getPoolAprByTokenPerSecond(
      //       stakingTokenPrice,
      //       earningTokenPrice,
      //       totalStaked,
      //       parseFloat(pool.tokenPerSecond),
      //     )
      //   : 0

      liveData.push({
        ...timeLimit,
        ...totalStaking,
        stakingTokenPrice,
        earningTokenPrice,
        apr: 0,
        isFinished: isPoolFinished,
      })
    }
    dispatch(setPoolsPublicData(liveData || []))
  } catch (error) {
    console.error('[Pools Action] error when getting public data', error)
  }
}

export const fetchNftPoolsUserDataAsync = createAsyncThunk<
  { sousId: number; isApprovedForAll: any; stakingNftBalance: any; stakedNftBalance: any; pendingReward: any }[],
  {
    account: string
    chainId: ChainId
  }
>('nftPools/fetchNftPoolsUserData', async ({ account, chainId }, { rejectWithValue }) => {
  try {
    const [isApprovedForAlls, stakingNftBalances, stakedNftBalances, pendingRewards] = await Promise.all([
      fetchNftPoolsIsApprovedForAll({ account, chainId, provider: getViemClients }),
      fetchUserNftBalances({ account, chainId, provider: getViemClients }),
      fetchUserStakedNftBalances({ account, chainId, provider: getViemClients }),
      fetchUserPendingRewards({ account, chainId, provider: getViemClients }),
    ])

    const poolsConfig = getNftPoolsConfig(chainId)
    const userData = poolsConfig?.map((pool) => ({
      sousId: pool.sousId,
      isApprovedForAll: isApprovedForAlls[pool.sousId],
      stakingNftBalance: stakingNftBalances[pool.sousId],
      stakedNftBalance: stakedNftBalances[pool.sousId],
      pendingReward: pendingRewards[pool.sousId],
    }))

    return userData
  } catch (e) {
    return rejectWithValue(e)
  }
})

export const updateUserIsApprovedForAll = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('nftPools/updateUserIsApprovedForAll', async ({ sousId, account, chainId }) => {
  const isApprovedForAlls = await fetchNftPoolsIsApprovedForAll({ account, chainId, provider: getViemClients })
  return { sousId, field: 'isApprovedForAll', value: isApprovedForAlls[sousId] }
})

export const updateUserNftBalance = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('nftPools/updateUserNftBalance', async ({ sousId, account, chainId }) => {
  const tokenBalances = await fetchUserNftBalances({ account, chainId, provider: getViemClients })
  return { sousId, field: 'stakingNftBalance', value: tokenBalances[sousId] }
})

export const updateUserStakedNftBalance = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('nftPools/updateUserStakedNftBalance', async ({ sousId, account, chainId }) => {
  const stakedBalances = await fetchUserStakedNftBalances({ account, chainId, provider: getViemClients })
  return { sousId, field: 'stakedNftBalance', value: stakedBalances[sousId] }
})

export const updateUserPendingReward = createAsyncThunk<
  { sousId: number; field: string; value: any },
  { sousId: number; account: string; chainId: ChainId }
>('nftPools/updateUserPendingReward', async ({ sousId, account, chainId }) => {
  const pendingRewards = await fetchUserPendingRewards({ chainId, account, provider: getViemClients })
  return { sousId, field: 'pendingReward', value: pendingRewards[sousId] }
})

const initialState: NftPoolsState = {
  data: [],
  userDataLoaded: false,
}
export const NftPoolsSlice = createSlice({
  name: 'nftPools',
  initialState,
  reducers: {
    setInitialPoolConfig: (state, action) => {
      const { chainId } = action.payload
      const poolsConfig = getNftPoolsConfig(chainId) || []
      state.data = [...poolsConfig]
      state.userDataLoaded = false
    },
    setPoolPublicData: (state, action) => {
      const { sousId } = action.payload
      const poolIndex = state.data.findIndex((pool) => pool.sousId === sousId)
      state.data[poolIndex] = {
        ...state.data[poolIndex],
        ...action.payload.data,
      }
    },
    setPoolUserData: (state, action) => {
      const { sousId } = action.payload
      state.data = state.data.map((pool) => {
        if (pool.sousId === sousId) {
          return { ...pool, userDataLoaded: true, userData: action.payload.data }
        }
        return pool
      })
    },
    setPoolsPublicData: (state, action) => {
      const livePoolsData: SerializedNftPool[] = action.payload
      const livePoolsSousIdMap = keyBy(livePoolsData, 'sousId')
      state.data = state.data.map((pool) => {
        const livePoolData = livePoolsSousIdMap[pool.sousId]
        return { ...pool, ...livePoolData }
      })
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetUserState, (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      state.data = state.data.map(({ userData, ...pool }) => {
        return { ...pool }
      })
      state.userDataLoaded = false
    })
    builder.addCase(
      fetchNftPoolsUserDataAsync.fulfilled,
      (
        state,
        action: PayloadAction<
          { sousId: number; isApprovedForAll: any; stakingNftBalance: any; stakedNftBalance: any; pendingReward: any }[]
        >,
      ) => {
        const userData = action.payload
        const userDataSousIdMap = keyBy(userData, 'sousId')
        state.data = state.data.map((pool) => ({
          ...pool,
          userDataLoaded: true,
          userData: userDataSousIdMap[pool.sousId],
        }))
        state.userDataLoaded = true
      },
    )
    builder.addCase(fetchNftPoolsUserDataAsync.rejected, (state, action) => {
      console.error('[Pools Action] Error fetching pool user data', action.payload)
    })
    builder.addMatcher(
      isAnyOf(
        updateUserIsApprovedForAll.fulfilled,
        updateUserNftBalance.fulfilled,
        updateUserStakedNftBalance.fulfilled,
        updateUserPendingReward.fulfilled,
      ),
      (state, action: PayloadAction<{ sousId: number; field: string; value: any }>) => {
        const { field, value, sousId } = action.payload
        const index = state.data.findIndex((p) => p.sousId === sousId)

        if (index >= 0) {
          state.data[index] = { ...state.data[index], userData: { ...state.data[index].userData, [field]: value } }
        }
      },
    )
  },
})

// Actions
export const { setPoolsPublicData, setPoolPublicData, setPoolUserData, setInitialPoolConfig } = NftPoolsSlice.actions

export default NftPoolsSlice.reducer
